import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Traec from "traec";
import { ErrorBoundary } from "storybook-dashboard/errors/handleError";
import RelatedRefs from "AppSrc/user/projectItem/relatedRefs";
import RelatedCommits from "AppSrc/user/projectItem/relatedCommits";
import { companyFields } from "../company";
import useApi, { doFetch } from "storybook-dashboard/utils/fetching";
import useToken from "storybook-dashboard/auth/token";
import { isSuperuser, listToMap } from "storybook-dashboard/utils";
import Im from "immutable";
import { useModal, Modal } from "storybook-dashboard/components/modal";
import { formEventToData } from "AppSrc/project/members/utils";
import Octicon from "react-octicon";
import { usePermission } from "AppSrc/utils/permissions";

const MODAL_ID = "CommonCompanyAddModal001";

function ProjectRow(props) {
  let { project, refs, depth } = props;
  let [collapsed, setCollapsed] = useState(true);

  if (!project) {
    return null;
  }

  let _projectId = (project?.get("uid") || "").substring(0, 8);

  // Count actual displayable refs (excluding "Not for" status)
  let displayableRefsCount = 0;
  let relatedRefs = project?.get("related_refs");
  if (relatedRefs?.size > 0) {
    displayableRefsCount = relatedRefs.filter((ref) => {
      let status = ref?.get("latest_status");
      let isOnHold = status?.startsWith("Not for");
      return !isOnHold;
    }).size;
  }

  // Count actual displayable commits (excluding staging and missing refId)
  let displayableCommitsCount = 0;
  let relatedCommits = project?.get("related_commits");
  if (relatedCommits?.size > 0) {
    displayableCommitsCount = relatedCommits.filter((commit) => {
      return !commit?.get("is_staging") && commit?.get("ref_id");
    }).size;
  }

  let totalDisplayableReports = displayableRefsCount + displayableCommitsCount;
  let hasReports = totalDisplayableReports > 0;

  // Check if user has permission to view this project
  //let { permissions, isLoading, isFetching } = usePermission({ projectId: project?.get("uid") });
  let hasPermission = true; //permissions?.get("actions")?.size > 0;
  let isCheckingPermissions = false; //isLoading || isFetching;

  return (
    <ErrorBoundary>
      <div className="card mb-1" style={{ marginLeft: `${Math.max(0, (depth - 1) * 0.1)}em` }}>
        <div className="card-body py-2">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <Octicon name="repo" className="text-info mr-2" />
              {isCheckingPermissions ? (
                <span className="font-weight-medium text-muted">{project?.get("name")}</span>
              ) : hasPermission ? (
                <Link className="font-weight-medium text-decoration-none" to={`/project/${_projectId}`}>
                  {project?.get("name")}
                </Link>
              ) : (
                <span className="font-weight-medium text-muted" title="You don't have permission to view this project">
                  {project?.get("name")} <Octicon name="lock" size="small" className="ml-1" />
                </span>
              )}
              {hasReports && (
                <button
                  className="btn btn-sm btn-link text-muted p-0 ml-2"
                  onClick={() => setCollapsed(!collapsed)}
                  title={collapsed ? "Show reports" : "Hide reports"}
                >
                  <Octicon name={collapsed ? "chevron-right" : "chevron-down"} />
                </button>
              )}
            </div>
            <div className="d-flex align-items-center">
              {hasReports && (
                <small className="text-muted mr-2">
                  {totalDisplayableReports} {totalDisplayableReports === 1 ? "report" : "reports"} with pending actions
                </small>
              )}
              {isCheckingPermissions ? (
                <button className="btn btn-sm btn-outline-secondary" disabled>
                  <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                </button>
              ) : hasPermission ? (
                <Link to={`/project/${_projectId}`} className="btn btn-sm btn-outline-info">
                  <Octicon name="arrow-right" size="small" />
                </Link>
              ) : (
                <button
                  className="btn btn-sm btn-secondary disabled"
                  disabled
                  title="You don't have permission to view this project"
                >
                  <Octicon name="lock" size="small" />
                </button>
              )}
            </div>
          </div>
          {!collapsed && hasReports && (
            <div className="mt-2 pt-2 border-top">
              <RelatedRefs project={project} depth={0} />
              <RelatedCommits project={project} refs={refs} depth={0} />
            </div>
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
}

function CompanyRow(props) {
  let { company, companies, projects } = props;
  let [collapsed, setCollapsed] = useState(false);

  let depth = company.get("depth") || 0;
  let parentCompany = depth === 1;
  let childids = company.get("childids") || Im.List();
  let children = childids.map((i) => companies?.get(i));

  let childProjectNameIds = company.get("projects") || Im.List();
  let childProjects = childProjectNameIds
    .sortBy((project) => project?.get("name"))
    .map((project, i) => (
      <ProjectRow key={i} {...props} project={projects?.get(project?.get("uid"))} depth={depth + 1} />
    ));

  // Recursive function to count all companies and projects in the subtree
  const countAllDescendants = (companyId) => {
    const currentCompany = companies?.get(companyId);
    if (!currentCompany) return { companies: 0, projects: 0 };

    let totalCompanies = 0;
    let totalProjects = 0;

    // Count direct child companies and recurse into them
    const childCompanyIds = currentCompany.get("childids") || Im.List();
    childCompanyIds.forEach((childId) => {
      totalCompanies += 1; // Count the child company itself
      const childCounts = countAllDescendants(childId);
      totalCompanies += childCounts.companies;
      totalProjects += childCounts.projects;
    });

    // Count direct child projects
    const projectIds = currentCompany.get("projects") || Im.List();
    totalProjects += projectIds.size;

    return { companies: totalCompanies, projects: totalProjects };
  };

  const descendantCounts = countAllDescendants(company.get("uid"));
  const totalDescendants = descendantCounts.companies + descendantCounts.projects;

  let _companyId = (company?.get("uid") || "").substring(0, 8);
  let hasChildren = children.size > 0 || childProjects.size > 0;

  // Check if user has permission to view this company using the usePermission hook
  //let { permissions, isLoading, isFetching } = usePermission({ companyId: _companyId });

  // User has permission if the permissions object has a size or length property
  let hasPermission = true; //permissions?.get("actions")?.size > 0;
  let isCheckingPermissions = false; //isLoading || isFetching;

  return (
    <ErrorBoundary>
      <div
        className={`card mb-1 ${parentCompany ? "border-primary" : "border-light"}`}
        style={{ marginLeft: `${Math.max(0, (depth - 1) * 0.5)}em` }}
      >
        <div className={`card-header ${parentCompany ? "bg-primary text-white" : "bg-light"} py-2`}>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <button
                className={`btn btn-sm btn-link p-0 mr-2 ${parentCompany ? "text-white" : "text-muted"}`}
                onClick={() => setCollapsed(!collapsed)}
                disabled={!hasChildren}
                title={hasChildren ? (collapsed ? "Expand" : "Collapse") : "No items to show"}
              >
                <Octicon name={hasChildren ? (collapsed ? "chevron-right" : "chevron-down") : "dash"} />
              </button>
              <Octicon name="organization" className={`mr-2 ${parentCompany ? "text-white" : "text-primary"}`} />
              {isCheckingPermissions ? (
                <span className={`font-weight-bold ${parentCompany ? "text-white-50" : "text-muted"}`}>
                  {company?.get("name")}
                </span>
              ) : hasPermission ? (
                <Link
                  className={`font-weight-bold text-decoration-none ${parentCompany ? "text-white" : "text-dark"}`}
                  to={`/company/${_companyId}`}
                >
                  {company?.get("name")}
                </Link>
              ) : (
                <span
                  className={`font-weight-bold ${parentCompany ? "text-white-50" : "text-muted"}`}
                  title="You don't have permission to view this dashboard"
                >
                  {company?.get("name")} <Octicon name="lock" size="small" className="ml-1" />
                </span>
              )}
            </div>
            <div className="d-flex align-items-center">
              {isCheckingPermissions ? (
                <button className={`btn btn-sm ${parentCompany ? "btn-light" : "btn-outline-secondary"}`} disabled>
                  <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                  <span className="sr-only">Loading...</span>
                </button>
              ) : hasPermission ? (
                <Link
                  to={`/company/${_companyId}`}
                  className={`btn btn-sm ${parentCompany ? "btn-light" : "btn-primary"}`}
                >
                  <Octicon name="arrow-right" size="small" className="mr-1" />
                  Dashboard
                </Link>
              ) : (
                <button
                  className={`btn btn-sm ${parentCompany ? "btn-light" : "btn-secondary"} disabled`}
                  disabled
                  title="You don't have permission to view this dashboard"
                >
                  <Octicon name="lock" size="small" className="mr-1" />
                  No Access
                </button>
              )}
            </div>
          </div>
        </div>
        {!collapsed && hasChildren && (
          <div className="card-body p-1">
            <ErrorBoundary>
              {children
                .sortBy((i) => i.get("name"))
                .map((child, i) => (
                  <CompanyRow key={i} {...props} company={child} />
                ))}
              {childProjects}
            </ErrorBoundary>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
}

const createCompany = ({ e, trigger, hideModal, setErrorMessage }) => {
  e.preventDefault();
  let body = formEventToData(e);

  // Validate company name
  if (!body.name || body.name.trim() === "") {
    setErrorMessage("Company name is required");
    return;
  }

  setErrorMessage(""); // Clear any existing error
  doFetch("/api/company/", "POST", body)
    .then((res) => {
      if (res.ok) {
        hideModal();
        trigger();
      } else {
        console.error("Failed to create company", res);
      }
    })
    .catch((err) => {
      console.error("Error creating company", err);
    });
};

export const AddCompanyFormModal = ({ trigger }) => {
  let { hideModal } = useModal();
  let [errorMessage, setErrorMessage] = useState("");
  let [companyName, setCompanyName] = useState("");

  const isValid = companyName.trim() !== "";

  return (
    <Modal title="Add a Company Account">
      <form onSubmit={(e) => createCompany({ e, trigger, hideModal, setErrorMessage })}>
        <div className="form-group">
          <label htmlFor="name">Company Name</label>
          <input
            type="text"
            className={`form-control ${errorMessage ? "is-invalid" : ""}`}
            id="name"
            name="name"
            placeholder="Company Name"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            required
          />
          {errorMessage && <div className="invalid-feedback">{errorMessage}</div>}
        </div>
        <button type="submit" className="btn btn-sm btn-primary" disabled={!isValid}>
          Create Company
        </button>
      </form>
    </Modal>
  );
};

export default function UserNotificationTree(props) {
  let [state, setState] = useState({});
  let [searchFilter, setSearchFilter] = useState("");
  let [viewMode, setViewMode] = useState("tree"); // "tree" or "list"
  let { token, decodeToken } = useToken();
  let { setModal } = useModal();
  let { setDashboardsCount } = props;

  let user = Im.fromJS(decodeToken(token));
  let { data: projects } = useApi("/api/project/");
  let { data: companies, trigger } = useApi("/api/company/");
  let { data: userRefs } = useApi("/api/tracker/ref/?light=true&includeOnHold=true");
  let { data: userCommits } = useApi("/api/tracker/commit/?light=true");

  // Count total dashboards (companies + projects) and update parent
  // This must be called before any conditional returns to follow Rules of Hooks
  useEffect(() => {
    if (setDashboardsCount && companies && projects) {
      const totalCount = companies.size + projects.size;
      console.log("UserNotificationTree counting dashboards:", {
        companies: companies.size,
        projects: projects.size,
        total: totalCount,
      });
      setDashboardsCount(totalCount);
    }
  }, [companies, projects, setDashboardsCount]);

  if (!projects || !companies || !userRefs || !userCommits) {
    return null;
  }

  let projectsById = listToMap(projects, "uid");
  let companiesById = listToMap(companies, "uid");
  let refsById = listToMap(userRefs, "uid");

  // Map the user refs onto the list of projects under related_refs
  projectsById = userRefs.reduce((acc, ref) => {
    let projectId = ref.get("project");
    let refId = ref.get("uid");
    return acc.updateIn([projectId, "related_refs"], Im.List(), (refs) => refs.push(ref));
  }, projectsById);

  // Nest the commits into the list of reporting packages under related_commits
  projectsById = userCommits.reduce((acc, commit) => {
    let projectId = commit?.get("project");
    //let refId = commit?.get("ref_id")
    return acc.updateIn([projectId, "related_commits"], Im.List(), (commits) => commits.push(commit));
  }, projectsById);

  // Filter companies and projects based on search
  let filteredCompanies = companies;
  let filteredProjects = projectsById;

  if (searchFilter.trim()) {
    const searchLower = searchFilter.toLowerCase();
    filteredCompanies = companies.filter((company) => company.get("name")?.toLowerCase().includes(searchLower));
    filteredProjects = projectsById.filter((project) => project?.get("name")?.toLowerCase().includes(searchLower));
  }

  let tree = filteredCompanies
    .filter((i) => i.get("depth") <= 1)
    .sortBy((i) => i.get("name"))
    .map((company, i) => (
      <CompanyRow
        key={i}
        {...props}
        company={company}
        companies={companiesById}
        projects={projectsById}
        refs={refsById}
      />
    ));

  let fetch = new Traec.Fetch("company", "post");
  fetch.updateFetchParams({
    postSuccessHook: () => $(`#${MODAL_ID}`).modal("hide"),
  });

  return (
    <ErrorBoundary>
      {/* Enhanced Header with Search and Controls */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="font-weight-bold mb-2">Dashboards and reports</h4>
          {/* <p className="text-muted mb-0">
            Dashboard Access: You have access to{" "}
            <span className="font-weight-medium">
              <Octicon name="organization" className="mr-1" />
              {filteredCompanies.size} companies, <Octicon name="repo" className="mr-1" />
              {filteredProjects.size} projects
            </span>
          </p> */}
        </div>
      </div>

      <div className="alert alert-light border-left-thick border-primary mb-4">
        <div className="d-flex align-items-center my-2">
          <small className="text-muted">
            <strong>Visual Key:</strong> <Octicon name="organization" className="mx-1" /> Companies{" "}
          </small>
        </div>
      </div>

      {/* Results */}
      {tree.size > 0 ? (
        <div className="mb-5">
          {searchFilter && (
            <div className="alert alert-info">
              <Octicon name="info" className="mr-2" />
              Showing {tree.size} result{tree.size !== 1 ? "s" : ""} for "{searchFilter}"
            </div>
          )}
          {tree}
        </div>
      ) : (
        <div className="text-center py-5">
          <Octicon name="search" size="large" className="text-muted mb-3" />
          <h5 className="text-muted">No results found</h5>
          <p className="text-muted">
            {searchFilter
              ? `No companies or projects match "${searchFilter}". Try adjusting your search terms.`
              : "You don't have access to any dashboards at this time."}
          </p>
          {searchFilter && (
            <button className="btn btn-outline-primary btn-sm" onClick={() => setSearchFilter("")}>
              <Octicon name="x" className="mr-1" />
              Clear search
            </button>
          )}
        </div>
      )}
    </ErrorBoundary>
  );
}

const mapStateToProps = (state, ownProps) => {
  let projects = state.getInPath("entities.projects.byId") || Traec.Im.Map();
  let companies = state.getInPath("entities.companies.byId") || Traec.Im.Map();
  let userRefs = state.getInPath("entities.refs.byId") || Traec.Im.Map();
  let commits = state.getInPath("entities.commits.byId") || Traec.Im.Map();

  // Map the responsible refs onto the list of projects
  let activeProjectRefs = userRefs
    .toList()
    .filter((cref) => cref.getInPath("latest_commit.discipline") && cref.get("depth") > 1)
    .map((cref) => {
      let projectId = cref.get("project");
      if (projects?.get(projectId)) {
        projects = projects.addListToSet(`${projectId}.related_refs`, [cref]);
      }
      return cref;
    });

  // Map the responsible commits onto  the list of projects
  commits
    .filter(
      (commit) =>
        commit &&
        Traec.Im.isImmutable(commit) &&
        commit?.getInPath("meta_json.actionRequiredBy") &&
        commit?.get("reporting_period")
    )
    .map((commit) => {
      let projectId = commit?.get("project");
      if (projects?.get(projectId)) {
        projects = projects.addListToSet(`${projectId}.related_commits`, [commit]);
      }
    });

  return {
    projects,
    activeProjectRefs,
    refs: userRefs,
    commits,
    companies,
    user: state.getInPath("auth.user"),
  };
};
